import { memo, MouseEvent, useEffect, useMemo, useRef, useState } from 'react';

import { useIsAllowedPermission } from '@mint-lib/profile-helpers';
import styled from '@mint-lib/styled';
import { format, parse } from 'date-fns';

import { Box, DatePicker } from '@myn/mui';

import { BUILDER_ROW_TYPES } from '../../../../../../constants/campaignBuilder.js';
import { PORTAL_CONTAINER } from '../../../../../../constants/myCampaigns.js';
import { useBuyingPlanInfoSelector } from '../../../../../../hooks/useGetBuyingPlanData.js';
import { TRACKING_EVENTS } from '../../../../../../types/performanceTracking.js';
import { BuilderTableColumn, BuilderTableRow } from '../../../../types.js';
import FormattedField from '../FormattedField/FormattedField.jsx';
import TableEditCell from '../TableEditCell/TableEditCell.jsx';
import { SERVER_DATE_FORMAT } from './constants.js';
import { getDateFormValue } from './utils.js';

type Props = {
  value: string;
  column: BuilderTableColumn;
  row: BuilderTableRow;
  cellColor: string;
};

const DateValueField = memo(({ value, column, row, cellColor }: Props) => {
  const selectedPlan = useBuyingPlanInfoSelector();

  const isAllowed = useIsAllowedPermission('campaignPlanningFeature:modify');

  const [localValue, setLocalValue] = useState<Date | null>(
    getDateFormValue(value),
  );
  const localValueRef = useRef<Date | null>(getDateFormValue(value));
  const [isOpened, setOpened] = useState<boolean>(false);
  const [isFocused, setFocused] = useState<boolean>(false);

  const {
    id: rowId,
    dateFrom,
    dateTo,
    budget,
    type,
    isCampaignCurrencyMode,
    isBuyingPlanLocked,
    isPredictivePlanningMode,
    isArchivedVersion,
    scale,
  } = row.original;
  const isDateFromColumn = column.id === 'dateFrom';
  const isDateToColumn = column.id === 'dateTo';

  const allowedDates = useMemo(() => {
    const minDate: Date | null = null;
    const maxDate: Date | null = null;

    if (!selectedPlan) {
      return { minDate, maxDate };
    }

    const mediaPlanStartDate = selectedPlan.dateFrom
      ? parse(selectedPlan.dateFrom, SERVER_DATE_FORMAT, new Date())
      : null;
    const mediaPlanEndDate = selectedPlan.dateTo
      ? parse(selectedPlan.dateTo, SERVER_DATE_FORMAT, new Date())
      : null;

    const selectedStartDate = dateFrom
      ? parse(dateFrom, SERVER_DATE_FORMAT, new Date())
      : null;
    const selectedEndDate = dateTo
      ? parse(dateTo, SERVER_DATE_FORMAT, new Date())
      : null;

    if (isDateFromColumn) {
      return {
        minDate: mediaPlanStartDate,
        maxDate: selectedEndDate || mediaPlanEndDate,
      };
    }

    if (isDateToColumn) {
      return {
        minDate: selectedStartDate || mediaPlanStartDate,
        maxDate: mediaPlanEndDate,
      };
    }

    return {
      minDate: mediaPlanStartDate,
      maxDate: mediaPlanEndDate,
    };
  }, [selectedPlan, dateFrom, dateTo]);

  const { startMeasurePerformance } = column;

  const isTotal = type === BUILDER_ROW_TYPES.TOTAL;
  const isTileRow = type === BUILDER_ROW_TYPES.TILE;
  const isReadOnly =
    !isTileRow ||
    isTotal ||
    !isAllowed ||
    isPredictivePlanningMode ||
    !!isArchivedVersion ||
    isCampaignCurrencyMode ||
    isBuyingPlanLocked;
  const isRequiredFieldsFilled = dateFrom && dateTo && budget;
  const isEmpty = !localValue;
  const isClearBtnShown =
    !isFocused && !isEmpty && !isReadOnly && !isRequiredFieldsFilled;

  const handleChange = (date: Date | null) => {
    setLocalValue(date);
    localValueRef.current = date;
  };

  const handleSubmit = () => {
    const formattedDate = localValueRef.current
      ? format(localValueRef.current, SERVER_DATE_FORMAT)
      : null;

    if (formattedDate !== value) {
      startMeasurePerformance(TRACKING_EVENTS.TABLE_UPDATE);
      column.updateRow(
        {
          id: rowId,
        },
        column.id,
        formattedDate,
      );
    }
  };

  const handleFocus = () => {
    if (isReadOnly) {
      return;
    }

    setOpened(true);
    setFocused(true);
  };

  const handleBlur = () => {
    handleSubmit();
    setOpened(false);
    setFocused(false);
  };

  const handleReset = (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    startMeasurePerformance(TRACKING_EVENTS.TABLE_UPDATE);
    column.updateRow(
      {
        id: rowId,
      },
      column.id,
      null,
    );
  };

  const handleKeyDown = (e: globalThis.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleBlur();
    }
  };

  useEffect(() => {
    if (isOpened) {
      globalThis.addEventListener('keydown', handleKeyDown);

      return () => {
        globalThis.removeEventListener('keydown', handleKeyDown);
      };
    }
  }, [isOpened]);

  useEffect(() => {
    const dateFormValue = getDateFormValue(value);
    setLocalValue(dateFormValue);
    localValueRef.current = dateFormValue;
  }, [value, isReadOnly]);

  if (isReadOnly) {
    return (
      <FormattedField
        scale={scale}
        value={localValue}
        color={cellColor}
        variant="date"
      />
    );
  }

  if (!isFocused) {
    return (
      <TableEditCell
        scale={scale}
        isDeleteButtonShown={isClearBtnShown}
        deleteValueFunc={handleReset}
        showEditModeFunc={handleFocus}
        startMeasurePerformance={startMeasurePerformance}
        value={localValue}
        variant="date"
      />
    );
  }

  return (
    <DatePickerWrapper $scale={scale}>
      <DatePicker
        fullWidth
        size="medium"
        selected={localValue}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        minDate={allowedDates.minDate}
        maxDate={allowedDates.maxDate}
        open={isOpened}
        onClickOutside={handleBlur}
        shouldCloseOnSelect={false}
        //@ts-ignore TODO:update with new calendar component after full migration
        portalId={PORTAL_CONTAINER}
        onCancel={handleBlur}
      />
    </DatePickerWrapper>
  );
});

const DatePickerWrapper = styled(Box)<{ $scale: number }>`
  height: 100%;
  width: 100%;
  & > div {
    height: 100%;
  }
  .react-datepicker-wrapper {
    height: 100%;
    width: 100%;
    overflow: hidden;
  }
  .react-datepicker-wrapper div {
    height: 100%;
    width: 100%;
  }
  & .MuiFormControl-root.MuiTextField-root {
    min-width: unset;
    height: 100%;
  }
  & .MuiInputBase-root {
    min-height: unset;
    height: 100%;
    box-shadow: inset 0 0 0 2px #72b1fc;
  }
  .MuiInputBase-root.MuiOutlinedInput-root.MuiInputBase-readOnly:hover {
    box-shadow: inset 0 0 0 2px #72b1fc;
  }
  .MuiInputBase-input + div {
    width: auto;
  }
  .react-datepicker-wrapper input {
    font-size: ${({ $scale }) => 0.875 * $scale}rem;
  }
  .react-datepicker-wrapper svg {
    ${({ $scale, theme }) => `
      transform: scale(${$scale});
      min-width: ${$scale > 1 ? `${$scale}rem` : 'unset'};
      color: ${theme.palette.text.primary};
    `};
  }
`;

export default DateValueField;
